import {CartData, CartProcessor} from "./CartProcessor";
import {Events} from "./CartEvents";
import {QuantityControl} from "./QuantityControl";
import {CartFeedback} from "./CartFeedback";
import {ScalePriceView} from "./ScalePriceView";
import {TabStrip} from "../utils/TabStrip";

/**
 * Helps to bring the content of the add2cart dialog to life. e.g. makes the plus/minus-buttons work.
 *
 * The root is the dialog (the elements are looked up inside it, the document for an older
 * caller), the signal the one of its Overlay: the listeners this class puts on the document go
 * when the dialog closes. Until 7.0.0 every opening left three of them behind, which went on
 * reacting to the cart events of the page.
 */
export class Add2CartDialogContent {
    static CONTAINER_QUERY_SELECTOR = '.imagetypeselection-container'
    static CART_BUTTON_QUERY_SELECTOR = '.eventgallery-opencart'
    static CLOSE_BUTTON_QUERY_SELECTOR = '.eventgallery-close-overlay'
    static ERROR_QUERY_SELECTOR = '.eventgallery-cart-error'

    /** Milliseconds between the last click on +/- and the request. */
    static COMMIT_DELAY = 300

    cartProcessor: CartProcessor;
    quantityControl: QuantityControl;
    root: ParentNode;
    signal: AbortSignal | undefined;
    cartButton : HTMLButtonElement
    /** the close button of the header and "Continue Shopping" in the footer */
    closeButtons : HTMLButtonElement[] = []

    /** True while a quantity is still on its way to the server. */
    private isBusy: boolean = false

    /** Where to go once the last request has come back. */
    private pendingHref: string | null = null

    constructor(cartProcessor: CartProcessor, root: ParentNode = document, signal?: AbortSignal) {
        this.cartProcessor = cartProcessor;
        this.root = root || document;
        this.signal = signal;
        this.initialize();
    }

    initialize() {

        this.cartButton = this.root.querySelector(Add2CartDialogContent.CART_BUTTON_QUERY_SELECTOR)
        this.closeButtons = Array.from(this.root.querySelectorAll<HTMLButtonElement>(Add2CartDialogContent.CLOSE_BUTTON_QUERY_SELECTOR))
        const listening = {signal: this.signal};

        // the tabs of the groups; only those of the dialog, never the tabs of the page
        const container = this.root.querySelector(Add2CartDialogContent.CONTAINER_QUERY_SELECTOR);
        if (container) {
            TabStrip.bindAll(container);
        }

        // Solange eine Mengenänderung noch unterwegs ist, darf der Dialog nicht
        // verlassen werden. Der Knopf zum Warenkorb navigiert erst, wenn die
        // letzte Anfrage zurück ist - ein Seitenwechsel bricht laufende
        // Anfragen ab, und der Artikel wäre dann im Browser sichtbar, aber nie
        // beim Server angekommen.
        document.addEventListener(Events.busy, (e: CustomEvent) => {
            this.isBusy = e.detail?.busy === true;
            this.updateToCartButton();
            if (!this.isBusy && this.pendingHref) {
                const href = this.pendingHref;
                this.pendingHref = null;
                document.location.href = href;
            }
        }, listening);

        document.addEventListener(Events.error, () => this.showError(), listening);
        document.addEventListener(Events.reload, (e: CustomEvent) => {
            this.syncQuantities(e.detail);
            this.updateFeedback(e.detail);
            ScalePriceView.update(this.root, e.detail);
        }, listening);
        this.updateFeedback(this.cartProcessor.getLastCartData());

        this.quantityControl = new QuantityControl(this.root, {
            debounceMs: Add2CartDialogContent.COMMIT_DELAY,
            onChange: () => this.updateToCartButton(),
            onCommit: (input, quantity) => {
                this.hideError();
                return this.cartProcessor.add(input.getAttribute('data-id'), quantity);
            },
            // The answer to this input's own request arrived while the input
            // still counted as busy, so it was left alone. Now it is free.
            onSettled: () => this.syncQuantities(this.cartProcessor.getLastCartData())
        });

        if (this.cartButton) {
            this.cartButton.addEventListener('click', (e) => this.openCart(e));
        }

        // Runs before the overlay's own handler removes the dialog, so a
        // change inside the delay still gets sent.
        this.closeButtons.forEach((button) => {
            button.addEventListener('click', () => this.quantityControl.flushAll(), {capture: true});
        });

        this.updateToCartButton()
    }

    /**
     * Zeigt an, dass der Warenkorb nicht erreicht wurde.
     *
     * Vorher blieb ein Fehlschlag unsichtbar: die Anfrage wurde abgeschickt und
     * niemand hat sich das Ergebnis angesehen.
     */
    showError() {
        let box: HTMLElement = this.root.querySelector(Add2CartDialogContent.ERROR_QUERY_SELECTOR);
        if (box) {
            box.hidden = false;
        }
    }

    hideError() {
        let box: HTMLElement = this.root.querySelector(Add2CartDialogContent.ERROR_QUERY_SELECTOR);
        if (box) {
            box.hidden = true;
        }
    }

    /**
     * Shows how many items are in the cart and what they cost.
     */
    updateFeedback(data: CartData | null) {
        CartFeedback.update(this.root, data);
    }

    /**
     * Takes the quantities the server confirmed into the inputs. That makes a
     * clamped quantity visible: the server cuts a quantity above the maximum
     * of the format down, and the dialog would otherwise keep showing the
     * number the visitor typed.
     */
    syncQuantities(data: CartData | null) {
        if (!data || !Array.isArray(data.cart) || !this.quantityControl) {
            return;
        }

        this.quantityControl.getInputs().forEach((input) => {
            const params = new URLSearchParams(input.getAttribute('data-id') || '');
            const folder = params.get('folder');
            const file = params.get('file');
            const typeid = params.get('imagetypeid');

            let quantity = 0;
            for (let i = 0; i < data.cart.length; i++) {
                const item = data.cart[i];
                if (item.folder === folder && item.file === file && String(item.typeid) === typeid) {
                    quantity = parseInt(item.count) || 0;
                    break;
                }
            }
            this.quantityControl.syncValue(input, quantity);
        });

        this.updateToCartButton();
    }

    updateToCartButton() {
        let sum = 0
        const inputs = this.quantityControl ? this.quantityControl.getInputs() : [];
        inputs.forEach((field) => {
            sum += parseInt(field.value) || 0;
        })

        const hasItems = sum > 0 || inputs.length === 0;

        if (this.cartButton) {
            this.cartButton.disabled = !hasItems || this.isBusy
        }

        // Auch das Schließen wartet: wer den Dialog zumacht und weiterklickt,
        // nimmt dieselbe abgebrochene Anfrage mit.
        this.closeButtons.forEach((button) => {
            button.disabled = this.isBusy
        });
    }

    /**
     * "Buy now": sends what is still pending, then goes to the cart once the
     * server has everything.
     */
    openCart(e: Event) {
        e.preventDefault();
        const href = this.cartButton.getAttribute('data-href');
        if (!href) {
            return;
        }

        this.quantityControl.flushAll();

        if (this.isBusy || this.cartProcessor.isBusy()) {
            this.pendingHref = href;
            this.updateToCartButton();
            return;
        }

        document.location.href = href;
    }
}
